import React, { useMemo, useState } from 'react';
import { Box, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';
import { AlertCircle, CheckCircle2, HelpCircle, RefreshCw } from 'lucide-react';
import SearchRunDistributions from './SearchRunDistributions';
import { METRICS, numberText } from '../../utils/portfolioDesign';
import { objectiveValue, trialStatusLabel } from '../../utils/portfolioDesignResults';
import { parameterLabel } from './PortfolioDesignEvidence';
import DataTable from '../data_table/DataTable';
import { sortByNumber, sortByText } from '../data_table/sorting';
import Chip from '../ui/Chip';
import Tooltip from '../ui/Tooltip';

const statusPresentation = {
    verified: { tone: 'success', Icon: CheckCircle2 },
    infeasible: { tone: 'danger', Icon: AlertCircle },
    unresolved: { tone: 'warning', Icon: HelpCircle },
    running: { tone: 'neutral', Icon: RefreshCw },
};

// The runs table is dense like the rule tables: 5px side padding, 0.75rem
// cells in tabular figures, header labels on up to two lines.
const RUNS_TABLE_CLASS_NAME = '![--tbl-cell-px:5px] [&_td]:text-[0.75rem]';
const RUNS_TABLE_HEIGHT = 360;
const INITIAL_SORTING = Object.freeze([{ id: 'number', desc: false }]);
const getRunRowId = (row) => row.index;
const isRunFinished = (row) => row.status !== 'running';
// Missing values read as undefined, so they sort last in either direction.
const isMissing = (spec, value) => value == null || (!spec.text && !Number.isFinite(value));

/**
 * The search runs, one row per trial: run number, status, objective, the
 * limits each run tested and its metrics. Columns have the px widths of
 * `columns`, which SearchRunDistributions below reuses, so the chart's axes
 * line up with the table's columns (the table's filler column takes any
 * extra width). Click a finished run, or press Enter or Space on it, to
 * choose it; hovering a row highlights its line in the chart.
 */
export default function PortfolioDesignRuns({ trials, dimensions, catalog, objective, selectedIndex, onChoose }) {
    const [showLines, setShowLines] = useState(true);
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const columns = useMemo(() => [
        { key: 'number', label: 'Run', width: 76, value: (row) => row.number, precision: 0 },
        { key: 'status', label: 'Status', width: 166, value: (row) => trialStatusLabel[row.status], text: true },
        { key: 'objective', label: `${objective.label} · ${objective.unit}`, width: 152, value: (row) => row.status === 'verified' ? objectiveValue(row, objective) : null, precision: objective.precision },
        ...dimensions.map(({ parameter }) => ({ key: parameter, label: `Auto · ${parameterLabel(parameter, catalog)}`, width: 164,
            value: (row) => row.parameters?.[parameter], precision: 4 })),
        ...['alpha_retention_pct', 'factor_vol_pct_aum', 'idio_vol_pct_aum', 'idio_variance_share_pct', 'turnover_pct_gmv']
            .filter((key) => key !== objective.key).map((key) => ({ key, label: `${METRICS[key].label} · ${METRICS[key].unit}`, width: 136,
                value: (row) => row.metrics?.[key], precision: METRICS[key].precision })),
    ], [dimensions, catalog, objective.key, objective.label, objective.unit, objective.precision]);
    const width = columns.reduce((sum, item) => sum + item.width, 0);
    // Rows tied on the sorted column (and missing values) stay in run order.
    const runs = useMemo(() => [...trials].sort((a, b) => a.number - b.number), [trials]);
    const tableColumns = useMemo(() => columns.map((spec) => ({
        id: spec.key,
        header: spec.label,
        size: spec.width,
        accessorFn: (row) => {
            const value = spec.value(row);
            return isMissing(spec, value) ? undefined : value;
        },
        sortFn: spec.text ? sortByText : sortByNumber,
        sortUndefined: 'last',
        cell: spec.key === 'status'
            ? ({ row: { original } }) => {
                const { tone, Icon } = statusPresentation[original.status];
                return (
                    <Tooltip content={original.status_message}>
                        <Chip icon={<Icon strokeWidth={2.25} />} label={trialStatusLabel[original.status]} tone={tone} />
                    </Tooltip>
                );
            }
            : ({ row: { original } }) => (spec.text ? spec.value(original) : numberText(spec.value(original), spec.precision)),
        meta: {
            align: spec.text || spec.key === 'number' ? 'left' : 'right',
            cellClassName: 'tabular-nums',
            wrap: spec.key === 'status',
        },
    })), [columns]);
    // One stable set of row props per run, so hovering re-renders no rows.
    const rowProps = useMemo(() => new Map(runs.map((row) => [row.index, {
        'aria-label': `Run ${row.number} · ${trialStatusLabel[row.status]}`,
        'aria-disabled': !isRunFinished(row),
        onMouseEnter: () => setHoveredIndex(row.index),
        onMouseLeave: () => setHoveredIndex(null),
    }])), [runs]);
    const selected = trials.find((row) => row.index === selectedIndex);
    const hovered = trials.find((row) => row.index === hoveredIndex && row.index !== selectedIndex);
    return <Box>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Click a finished run to inspect it. Ready portfolios load into the table above; save the ones you want to keep. Auto columns show the limits tested in that run.
        </Typography>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} sx={{ minHeight: 34, mb: 0.5 }}>
            <FormControlLabel sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
                control={<Checkbox size="small" checked={showLines} onChange={(_, checked) => setShowLines(checked)} />} label="Show run lines" />
            <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap" justifyContent="flex-end">
                {selected && <Typography variant="caption" color="error.main" fontWeight={600}>Run {selected.number} selected</Typography>}
                {hovered && <Typography variant="caption" color="primary.main">Run {hovered.number} hovered</Typography>}
                {!selected && !hovered && <Typography variant="caption" color="text.secondary">Select a run to highlight its values</Typography>}
            </Stack>
        </Stack>
        <Box sx={{ overflowX: 'auto' }} tabIndex={0} aria-label="Scrollable search runs">
        <Box sx={{ width: width + 18 }}>
            <DataTable
                aria-label="Search runs"
                data={runs}
                columns={tableColumns}
                getRowId={getRunRowId}
                initialSorting={INITIAL_SORTING}
                selectedRowId={selectedIndex ?? null}
                onRowClick={(row) => onChoose(row.index)}
                isRowClickable={isRunFinished}
                getRowProps={(row) => rowProps.get(row.index)}
                emptyMessage="Runs will appear here as the search progresses."
                className={RUNS_TABLE_CLASS_NAME}
                height={RUNS_TABLE_HEIGHT}
                wrapHeaders
                virtualize
            />
            <SearchRunDistributions columns={columns} trials={trials} width={width} showLines={showLines}
                selectedIndex={selectedIndex} hoveredIndex={hoveredIndex} onHover={setHoveredIndex} onChoose={onChoose} />
        </Box>
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            Bars show the distribution in each column. Each column uses its own scale; lines connect values from one run. Missing values leave a gap.
        </Typography>
    </Box>;
}
